import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Button, Input, Modal, Spin, Table, Tooltip, message } from "antd";
import { deleteSprStatus, fetchSprStatusList } from "./spr-status-api";
import SprStatus from "./spr-status";
import {
  colorSwatch,
  getLawFlag,
  normalizeStatus,
  pick,
  toNumber
} from "./spr-status-utils";
import "./spr-statuses.css";

const qFromSearchParams = (params) => {
  const src =
    params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  return src.has("q") ? src.get("q") ?? "" : "";
};

/**
 * Список статусов. Delphi: TFrmSprStatus.
 */
const SprStatuses = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const drawerOpen = isNew || editId != null;

  const urlQ = useMemo(() => qFromSearchParams(searchParams), [searchParams]);

  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 43) === 1;

  const [items, setItems] = useState([]);
  const [nameFilter, setNameFilter] = useState(() => urlQ);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const listReqRef = useRef(0);
  const skipUrlSyncRef = useRef(false);

  useEffect(() => {
    const q = (nameFilter || "").trim();
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (searchParams.toString() === next.toString()) return;
    skipUrlSyncRef.current = true;
    setSearchParams(next, { replace: true });
  }, [nameFilter, searchParams, setSearchParams]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const fromUrl = qFromSearchParams(searchParams);
    setNameFilter((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [searchParams]);

  const loadList = useCallback(() => {
    const requestId = ++listReqRef.current;
    setLoadingList(true);
    setError("");
    return fetchSprStatusList()
      .then((data) => {
        if (requestId !== listReqRef.current) return;
        const rows = Array.isArray(data)
          ? data.map(normalizeStatus).filter((r) => r.idStatus != null)
          : [];
        setItems(rows);
        setSelectedId((prev) => {
          if (prev != null && rows.some((r) => r.idStatus === prev)) return prev;
          return null;
        });
      })
      .catch((e) => {
        if (requestId !== listReqRef.current) return;
        setError(e.message || "Не удалось загрузить статусы");
        setItems([]);
      })
      .finally(() => {
        if (requestId === listReqRef.current) setLoadingList(false);
      });
  }, []);

  useEffect(() => {
    if (canEdit) loadList();
  }, [loadList, canEdit]);

  useEffect(() => {
    if (editId != null) setSelectedId(editId);
  }, [editId]);

  const filteredItems = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(q) ||
        String(t.idStatus).includes(q) ||
        String(t.idx).includes(q)
    );
  }, [items, nameFilter]);

  const selectedItem = useMemo(
    () => items.find((t) => t.idStatus === selectedId) || null,
    [items, selectedId]
  );

  const openCreate = () => {
    if (!canEdit) return;
    navigate("/spr/status/new");
  };

  const openEdit = (row) => {
    if (row?.idStatus == null) return;
    setSelectedId(row.idStatus);
    navigate(`/spr/status/${row.idStatus}`);
  };

  const closeDrawer = () => {
    navigate("/spr/status");
  };

  const onDrawerSaved = (saved) => {
    const id = toNumber(pick(saved, "idStatus", "IdStatus"));
    loadList().then(() => {
      if (id != null) {
        setSelectedId(id);
        navigate(`/spr/status/${id}`, { replace: true });
      }
    });
  };

  const onDel = () => {
    if (!selectedItem || !canEdit) return;
    Modal.confirm({
      title: "Удалить?",
      content: `Статус «${selectedItem.name || selectedItem.idStatus}» будет удалён.`,
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () =>
        deleteSprStatus(selectedItem.idStatus)
          .then(() => {
            message.success("Статус удалён");
            if (editId === selectedItem.idStatus) closeDrawer();
            setSelectedId(null);
            return loadList();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  };

  const columns = [
    {
      title: "Код",
      dataIndex: "idStatus",
      key: "idStatus",
      width: 72,
      align: "right"
    },
    {
      title: "Порядок",
      dataIndex: "idx",
      key: "idx",
      width: 90,
      align: "right"
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      render: (name, row) => name || `#${row.idStatus}`
    },
    {
      title: "Цвет",
      dataIndex: "color",
      key: "color",
      width: 80,
      align: "center",
      render: (v) => {
        const sw = colorSwatch(v);
        return <span title={sw.hex} style={sw.style} />;
      }
    }
  ];

  if (!canEdit) {
    return (
      <div className="spr-statuses">
        <Alert type="warning" showIcon message="Нет права на справочник статусов" />
      </div>
    );
  }

  return (
    <div className="spr-statuses">
      {error ? (
        <Alert className="spr-statuses__error" type="error" showIcon message={error} />
      ) : null}

      <div className="spr-statuses__body">
        <div className="spr-statuses__list">
          <div className="spr-statuses__toolbar">
            <div className="spr-statuses__extra-actions">
              <Tooltip title="Создать">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  aria-label="Создать"
                  onClick={openCreate}
                />
              </Tooltip>
              <Tooltip title={selectedItem ? "Изменить" : "Выберите запись"}>
                <span>
                  <Button
                    icon={<EditOutlined />}
                    disabled={!selectedItem}
                    aria-label="Изменить"
                    onClick={() => openEdit(selectedItem)}
                  />
                </span>
              </Tooltip>
              <Tooltip title={selectedItem ? "Удалить" : "Выберите запись"}>
                <span>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!selectedItem}
                    aria-label="Удалить"
                    onClick={onDel}
                  />
                </span>
              </Tooltip>
            </div>
            <Input
              allowClear
              variant="borderless"
              className="spr-statuses__filter"
              placeholder="Фильтр по наименованию"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>

          <div className="spr-statuses__list-body">
            {loadingList ? (
              <div className="spr-statuses__center">
                <Spin />
              </div>
            ) : (
              <Table
                className="spr-statuses__table"
                size="small"
                rowKey="idStatus"
                pagination={false}
                columns={columns}
                dataSource={filteredItems}
                rowClassName={(row) =>
                  [
                    "spr-statuses__row",
                    row.idStatus === selectedId ? "spr-statuses__row--selected" : "",
                    editId != null && row.idStatus === editId ? "spr-statuses__row--editing" : ""
                  ]
                    .filter(Boolean)
                    .join(" ")
                }
                onRow={(row) => ({
                  onClick: () => {
                    setSelectedId(row.idStatus);
                    openEdit(row);
                  }
                })}
                locale={{
                  emptyText: items.length ? "Ничего не найдено" : "Нет статусов"
                }}
              />
            )}
          </div>
        </div>
      </div>

      <SprStatus
        open={drawerOpen}
        idStatus={editId}
        isNew={isNew}
        canEdit={canEdit}
        onClose={closeDrawer}
        onSaved={onDrawerSaved}
      />
    </div>
  );
};

export default SprStatuses;
